import { useState } from "react";
import { ChevronLeft, ChevronRight, UserPlus, UserCheck } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useToast } from "../../hooks/use-toast";
import { useNavigate } from "react-router-dom";
import OnlineFriendsSidebar from "./OnlineFriendsSidebar";


const friendRequests = [
{ id: 1, name: "Orkhan Bagirov", initials: "OB", uni: "ASOIU", major: "Software Eng.", mutuals: 4 },
{ id: 2, name: "Vusal Tahirov", initials: "VT", uni: "BSU", major: "Physics", mutuals: 2 },
{ id: 3, name: "Narmin Sultanova", initials: "NS", uni: "ADA", major: "Economics", mutuals: 7 },
{ id: 4, name: "Samir Dadashov", initials: "SD", uni: "BEU", major: "Mechanical Eng.", mutuals: 1 },
{ id: 5, name: "Lamiya Farajova", initials: "LF", uni: "BSU", major: "Mathematics", mutuals: 5 }];


const suggestedFriends = [
{ name: "Kamran Hasanov", initials: "KH", uni: "BSU", major: "Computer Science", mutuals: 5 },
{ name: "Sabina Rzayeva", initials: "SR", uni: "ADA", major: "Business Admin", mutuals: 3 },
{ name: "Elvin Mammadov", initials: "EM", uni: "BSU", major: "Mathematics", mutuals: 8 },
{ name: "Gulnar Aliyeva", initials: "GA", uni: "BEU", major: "Engineering", mutuals: 2 },
{ name: "Farid Guliyev", initials: "FG", uni: "BSU", major: "Physics", mutuals: 4 },
{ name: "Aysel Huseynova", initials: "AH", uni: "ADA", major: "Economics", mutuals: 6 }];


const CommunityRightSidebar = () => {
  const [requestPage, setRequestPage] = useState(0);
  const [friendPage, setFriendPage] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(friendRequests);
  const [followedFriends, setFollowedFriends] = useState([]);
  const { toast } = useToast();
  const navigate = useNavigate();

  // Friend requests pagination
  const reqPerPage = 2;
  const totalReqPages = Math.ceil(pendingRequests.length / reqPerPage);
  const visibleRequests = pendingRequests.slice(requestPage * reqPerPage, (requestPage + 1) * reqPerPage);

  // Suggested friends pagination
  const friendsPerPage = 2;
  const totalFriendPages = Math.ceil(suggestedFriends.length / friendsPerPage);
  const visibleFriends = suggestedFriends.slice(friendPage * friendsPerPage, (friendPage + 1) * friendsPerPage);

  const handleAccept = (id, name) => {
    setPendingRequests((prev) => prev.filter((r) => r.id !== id));
    toast({ title: `Accepted ${name}'s friend request` });
  };

  const handleDecline = (id, name) => {
    setPendingRequests((prev) => prev.filter((r) => r.id !== id));
    toast({ title: `Declined ${name}'s request` });
  };

  const handleFollow = (idx, name) => {
    const isFollowed = followedFriends.includes(idx);
    setFollowedFriends((prev) => isFollowed ? prev.filter((f) => f !== idx) : [...prev, idx]);
    toast({
      title: isFollowed ? `Unfollowed ${name}` : `You are now following ${name}`,
      description: isFollowed ? undefined : "You'll see their activity in your feed."
    });
  };

  return (
    <div className="space-y-4">
      {/* Friend Requests */}
      <div className="stat-card !p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-display font-bold text-foreground flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-secondary" />
            Friend Requests
            {pendingRequests.length > 0 &&
            <span className="ml-1 w-5 h-5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
                {pendingRequests.length}
              </span>
            }
          </h3>
          {totalReqPages > 1 &&
          <div className="flex items-center gap-1">
              <button onClick={() => setRequestPage(Math.max(0, requestPage - 1))} disabled={requestPage === 0}
            className="w-6 h-6 rounded-md bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80">
                <ChevronLeft className="w-3 h-3" />
              </button>
              <span className="text-[10px] text-muted-foreground font-medium">{requestPage + 1}/{totalReqPages}</span>
              <button onClick={() => setRequestPage(Math.min(totalReqPages - 1, requestPage + 1))} disabled={requestPage >= totalReqPages - 1}
            className="w-6 h-6 rounded-md bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80">
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          }
        </div>
        {pendingRequests.length === 0 ?
        <p className="text-xs text-muted-foreground text-center py-3">No pending requests</p> :

        <div className="space-y-2.5">
            {visibleRequests.map((r) =>
          <div key={r.id} className="p-2.5 rounded-xl bg-muted/50 hover:bg-muted transition-colors">
                <div className="flex items-center gap-2.5 mb-2">
                  <button onClick={() => navigate(`/profile/${r.id}`)}>
                    <Avatar className="w-9 h-9">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{r.initials}</AvatarFallback>
                    </Avatar>
                  </button>
                  <div className="flex-1 min-w-0">
                    <button onClick={() => navigate(`/profile/${r.id}`)} className="text-sm font-medium text-foreground truncate hover:text-secondary transition-colors block">
                      {r.name}
                    </button>
                    <p className="text-[10px] text-muted-foreground">{r.uni} · {r.major}</p>
                    <p className="text-[10px] text-muted-foreground">{r.mutuals} mutual friends</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleAccept(r.id, r.name)}
              className="flex-1 py-1.5 rounded-lg bg-secondary text-secondary-foreground text-xs font-semibold hover:bg-secondary/90 transition-colors">
                    Accept
                  </button>
                  <button onClick={() => handleDecline(r.id, r.name)}
              className="flex-1 py-1.5 rounded-lg bg-muted text-muted-foreground text-xs font-semibold hover:bg-destructive/10 hover:text-destructive transition-colors">
                    Decline
                  </button>
                </div>
              </div>
          )}
          </div>
        }
      </div>

        <OnlineFriendsSidebar />

      {/* Suggested Friends */}
      <div className="stat-card !p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-display font-bold text-foreground flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-secondary" /> Suggested Friends
          </h3>
          <div className="flex items-center gap-1">
            <button onClick={() => setFriendPage(Math.max(0, friendPage - 1))} disabled={friendPage === 0}
            className="w-6 h-6 rounded-md bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80">
              <ChevronLeft className="w-3 h-3" />
            </button>
            <button onClick={() => setFriendPage(Math.min(totalFriendPages - 1, friendPage + 1))} disabled={friendPage >= totalFriendPages - 1}
            className="w-6 h-6 rounded-md bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80">
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
        <div className="space-y-2.5">
          {visibleFriends.map((f, i) => {
            const globalIdx = friendPage * friendsPerPage + i;
            return (
              <div key={i} className="flex items-center gap-2.5 p-2 rounded-xl hover:bg-muted/50 transition-colors">
                <button onClick={() => navigate(`/profile/${globalIdx + 100}`)}>
                  <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{f.initials}</AvatarFallback>
                  </Avatar>
                </button>
                <div className="flex-1 min-w-0">
                  <button onClick={() => navigate(`/profile/${globalIdx + 100}`)} className="text-sm font-medium text-foreground truncate hover:text-secondary transition-colors block">
                    {f.name}
                  </button>
                  <p className="text-xs text-muted-foreground">{f.uni} · {f.major}</p>
                  <p className="text-xs text-muted-foreground">{f.mutuals} mutual</p>
                </div>
                <button onClick={() => handleFollow(globalIdx, f.name)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-colors ${
                followedFriends.includes(globalIdx) ? "bg-secondary text-secondary-foreground" : "bg-secondary/10 text-secondary hover:bg-secondary/20"}`
                }>
                  {followedFriends.includes(globalIdx) ? "Following" : "Follow"}
                </button>
              </div>);

          })}
        </div>
      </div>

    </div>);

};

export default CommunityRightSidebar;